import { DateTime } from 'luxon';

import { DispatchTripRead } from 'src/common/external/bambi-api/bambiApi';

/**
 * From a set of potential multiload trip IDs (already filtered by time window,
 * payer compatibility, etc.), identifies existing multi-load trips on the
 * assignment.
 *
 * When `filterToClosestMultiload` is false (default): returns the IDs of all
 * candidate trips that already have a multiload_id (old behavior).
 *
 * When `filterToClosestMultiload` is true: groups candidates by multiload_id,
 * picks the multi-load closest in time to the originating trip, and expands
 * the result to include every trip in that multi-load on the assignment.
 * Ties keep the first group encountered in assignedTrips order.
 */
export function getExistingMultiloadTripIdsForAssignment(
  potentialMultiloadTripIds: string[],
  assignedTrips: DispatchTripRead[] | undefined,
  originatingTrip: DispatchTripRead,
  filterToClosestMultiload = false,
  assignmentId?: string
): string[] {
  if (!assignedTrips || potentialMultiloadTripIds.length === 0) return [];

  const potentialSet = new Set(potentialMultiloadTripIds);

  // Scope to the target assignment to prevent cross-assignment expansion
  const scopedTrips = assignmentId
    ? assignedTrips.filter((t) => t.assignment_id === assignmentId)
    : assignedTrips;

  if (!filterToClosestMultiload) {
    // Old behavior: return IDs of candidate trips that have a multiload_id.
    return scopedTrips
      .filter(
        (t) => potentialSet.has(t.id) && typeof t.multiload_id === 'string'
      )
      .map((t) => t.id);
  }

  // New behavior: group by multiload_id, pick closest, expand to full membership.

  // Step A: From the candidates, find trips that are already in a multi-load
  // and group them by multiload_id.
  const groupsByMultiloadId = new Map<string, DispatchTripRead[]>();
  for (const trip of scopedTrips) {
    if (potentialSet.has(trip.id) && typeof trip.multiload_id === 'string') {
      const group = groupsByMultiloadId.get(trip.multiload_id);
      if (group) {
        group.push(trip);
      } else {
        groupsByMultiloadId.set(trip.multiload_id, [trip]);
      }
    }
  }

  if (groupsByMultiloadId.size === 0) return [];

  // Pick the best multiload_id — closest in time to the originating trip.
  // Ties intentionally keep the first encountered group by only updating
  // when we find a strictly smaller distance.
  let bestMultiloadId: string | undefined;

  if (groupsByMultiloadId.size === 1) {
    bestMultiloadId = groupsByMultiloadId.keys().next().value;
  } else {
    const originPickup = DateTime.fromISO(
      originatingTrip.scheduled_pickup_at
    );
    let bestDistance = Infinity;

    for (const [multiloadId, trips] of groupsByMultiloadId) {
      for (const trip of trips) {
        const distance = Math.abs(
          originPickup.diff(DateTime.fromISO(trip.scheduled_pickup_at))
            .milliseconds
        );
        if (distance < bestDistance) {
          bestDistance = distance;
          bestMultiloadId = multiloadId;
        }
      }
    }
  }

  if (!bestMultiloadId) return [];

  // Step B: Expand — return ALL trips on this assignment that belong to
  // the chosen multi-load, not just the ones in potentialMultiloadTripIds.
  return scopedTrips
    .filter((t) => t.multiload_id === bestMultiloadId)
    .map((t) => t.id);
}
